/*
Copyright (C) 2026  Integrated Tech9labs Pvt. Ltd.
This program is free software: you can redistribute it and/or modify
it under the terms of the GNU Affero General Public License as
published by the Free Software Foundation, either version 3 of the
License, or (at your option) any later version.
This program is distributed in the hope that it will be useful,
but WITHOUT ANY WARRANTY; without even the implied warranty of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
GNU Affero General Public License for more details.
You should have received a copy of the GNU Affero General Public License
along with this program.  If not, see <http://www.gnu.org/licenses/>.
*/

import * as React from "react";
import { observer } from "mobx-react";
import styled from "styled-components";
import { Activity, CircleX, Gauge, HardDriveDownload, Search, Timer, Waves } from "lucide-react";

import EmptyState from "@src/components/ui/Lists/EmptyState";
import LiveExecutionCard from "@src/components/modules/LiveModule/LiveExecutionCard";
import Sparkline from "@src/components/modules/LiveModule/Sparkline";
import { Kpi, KpiGrid } from "@src/components/modules/LiveModule/ui/LiveUi";
import type { LiveEntry, ThroughputSample } from "@src/components/modules/LiveModule/LiveStore";
import { LIVE, endpointOf, formatClock, providerLabel } from "@src/components/modules/LiveModule/liveUtils";
import type { Project } from "@src/@types/Project";
import {
  formatBytes,
  formatDuration,
  formatRate,
  isActiveStatus,
  parseApiDate,
} from "@src/utils/TransferProgress";

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  padding-bottom: 48px;
`;
const Filters = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
`;
const Segmented = styled.div`
  display: inline-flex;
  max-width: 100%;
  overflow-x: auto; /* a phone scrolls the tabs, not the page */
  background: var(--t9-bg-surface);
  border: 1px solid var(--t9-border-control);
  border-radius: 6px;
  padding: 2px;
  gap: 2px;
`;
const SegButton = styled.button<{ $on: boolean }>`
  font: inherit;
  font-size: 13px;
  font-weight: ${p => (p.$on ? 600 : 500)};
  height: 28px;
  padding: 0 12px;
  border-radius: 4px;
  border: none;
  cursor: pointer;
  background: ${p => (p.$on ? LIVE.slate : "transparent")};
  color: ${p => (p.$on ? "#fff" : LIVE.textSecondary)};
  display: inline-flex;
  align-items: center;
  gap: 6px;
  &:hover {
    background: ${p => (p.$on ? LIVE.slate : "var(--t9-bg-sunken)")};
  }
  &:focus-visible {
    outline: 2px solid ${LIVE.cyan};
    outline-offset: 1px;
  }
  span.n {
    font-size: 11px;
    font-weight: 600;
    min-width: 18px;
    height: 18px;
    padding: 0 5px;
    border-radius: 9px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background: ${p => (p.$on ? "rgba(255,255,255,0.2)" : "var(--t9-bg-sunken)")};
  }
`;
const Select = styled.select`
  font: inherit;
  font-size: 13px;
  height: 34px;
  padding: 0 28px 0 10px;
  border: 1px solid var(--t9-border-control);
  border-radius: 6px;
  background: var(--t9-bg-surface)
    url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%2364748B' stroke-width='2.5' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='m6 9 6 6 6-6'/%3E%3C/svg%3E")
    no-repeat right 9px center;
  appearance: none;
  color: ${LIVE.text};
  cursor: pointer;
  &:focus-visible {
    outline: 2px solid ${LIVE.cyan};
    outline-offset: 1px;
  }
`;
const SearchBox = styled.label`
  display: flex;
  align-items: center;
  gap: 6px;
  height: 34px;
  padding: 0 10px;
  border: 1px solid var(--t9-border-control);
  border-radius: 6px;
  background: var(--t9-bg-surface);
  flex: 1 1 200px;
  max-width: 320px;
  margin-left: auto;
  input {
    border: none;
    outline: none;
    font: inherit;
    font-size: 13px;
    width: 100%;
    color: ${LIVE.text};
    background: transparent;
  }
  &:focus-within {
    outline: 2px solid ${LIVE.cyan};
    outline-offset: 1px;
  }
`;
const Cards = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;
const SectionTitle = styled.h2`
  margin: 8px 0 0 0;
  font-size: 13px;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: ${LIVE.muted};
`;
const EmptyCard = styled.div`
  background: var(--t9-bg-surface);
  border: 1px solid ${LIVE.border};
  border-radius: 8px;
`;
const Updated = styled.div`
  font-size: 12px;
  color: ${LIVE.muted};
  display: flex;
  align-items: center;
  gap: 6px;
  &::before {
    content: "";
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background: ${LIVE.green};
  }
`;

export type StatusFilter = "active" | "running" | "queued" | "finished";
export type TypeFilter = "all" | "replica" | "migration" | "deployment";

export const isQueued = (e: LiveEntry) =>
  isActiveStatus(e.status) &&
  (e.progress.phase === "Queued" || e.status === "PENDING" || e.status === "AWAITING_MINION_ALLOCATIONS");

type Props = {
  entries: LiveEntry[];
  samples: { [key: string]: ThroughputSample[] };
  totals: ThroughputSample[];
  failedLast24h: number;
  projects: Project[];
  loaded: boolean;
  lastUpdated: number | null;
  now: number;
  onCreateTransfer: () => void;
};

const iconFor = (Icon: any) => <Icon size={16} color={LIVE.faint} strokeWidth={1.9} aria-hidden="true" />;

const startOfToday = (now: number) => {
  const d = new Date(now);
  d.setHours(0, 0, 0, 0);
  return d.getTime();
};

const LiveMonitorContent = observer((props: Props) => {
  const [status, setStatus] = React.useState<StatusFilter>("active");
  const [type, setType] = React.useState<TypeFilter>("all");
  const [source, setSource] = React.useState("");
  const [target, setTarget] = React.useState("");
  const [project, setProject] = React.useState("");
  const [query, setQuery] = React.useState("");
  const { entries, now } = props;

  const running = entries.filter(e => isActiveStatus(e.status) && !isQueued(e));
  const queued = entries.filter(isQueued);
  const finished = entries.filter(e => !isActiveStatus(e.status));
  const active = [...running, ...queued];

  // KPIs
  const moving = running.reduce((a, e) => a + e.progress.rateBytesPerSec, 0);
  const copyingDisks = running.reduce((a, e) => a + e.progress.activeDisks, 0);
  const today = startOfToday(now);
  const todays = entries.filter(e => (parseApiDate(e.updatedAt) || parseApiDate(e.createdAt) || 0) >= today);
  const copiedToday = todays.reduce((a, e) => a + e.progress.doneBytes, 0);
  const withEta = running.filter(e => e.progress.etaSeconds != null && e.progress.etaSeconds > 0);
  const longest = withEta.sort((a, b) => (b.progress.etaSeconds || 0) - (a.progress.etaSeconds || 0))[0];
  const syncing = running.filter(e => e.progress.phase === "Syncing disks").length;

  const sourceTypes = Array.from(new Set(entries.map(e => endpointOf(e.sourceEndpointId)?.type).filter(Boolean))) as string[];
  const targetTypes = Array.from(new Set(entries.map(e => endpointOf(e.targetEndpointId)?.type).filter(Boolean))) as string[];
  const projectIds = Array.from(new Set(entries.map(e => e.projectId).filter(Boolean))) as string[];

  const q = query.trim().toLowerCase();
  const pool = status === "active" ? active : status === "running" ? running : status === "queued" ? queued : finished;
  const shown = pool.filter(e => {
    if (type === "deployment" && e.itemType !== "deployment") return false;
    if (type === "replica" && (e.itemType !== "transfer" || e.kind !== "replica")) return false;
    if (type === "migration" && (e.itemType !== "transfer" || e.kind !== "migration")) return false;
    const src = endpointOf(e.sourceEndpointId);
    const dst = endpointOf(e.targetEndpointId);
    if (source && src?.type !== source) return false;
    if (target && dst?.type !== target) return false;
    if (project && e.projectId !== project) return false;
    if (q) {
      const hay = [e.name, ...e.instances, src?.name, dst?.name, providerLabel(src?.type), providerLabel(dst?.type)]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      if (!hay.includes(q)) return false;
    }
    return true;
  });
  const filtered = Boolean(type !== "all" || source || target || project || q);

  const kpis = (
    <KpiGrid $min={200}>
      <Kpi
        label="Active migrations"
        icon={iconFor(Activity)}
        value={active.length}
        loading={!props.loaded}
        sub={
          active.length ? (
            <>
              <b>{syncing}</b> syncing · <b>{queued.length}</b> queued
            </>
          ) : (
            "Nothing running"
          )
        }
      />
      <Kpi
        label="Data moving now"
        icon={iconFor(Waves)}
        value={moving > 0 ? formatRate(moving) : "0 MiB/s"}
        loading={!props.loaded}
        sub={copyingDisks ? `${copyingDisks} disk${copyingDisks === 1 ? "" : "s"} copying` : "No disk is copying"}
        spark={<Sparkline values={props.totals.map(t => t.rate)} height={28} min={0} label="Combined throughput" />}
      />
      <Kpi
        label="Data copied today"
        icon={iconFor(HardDriveDownload)}
        value={formatBytes(copiedToday)}
        loading={!props.loaded}
        sub={`${todays.length} run${todays.length === 1 ? "" : "s"} today`}
      />
      <Kpi
        label="Longest ETA"
        icon={iconFor(Timer)}
        value={longest ? `~${formatDuration(longest.progress.etaSeconds)}` : "—"}
        loading={!props.loaded}
        sub={
          longest ? (
            <>
              <b>{longest.name}</b> · finishes {formatClock(now + (longest.progress.etaSeconds || 0) * 1000, now)}
            </>
          ) : (
            "No copy with an estimate"
          )
        }
      />
      <Kpi
        label="Failed in last 24 h"
        icon={iconFor(CircleX)}
        value={props.failedLast24h}
        tone={props.failedLast24h ? "danger" : null}
        loading={!props.loaded}
        to={props.failedLast24h ? "/transfers" : undefined}
        sub={props.failedLast24h ? "Open the transfers to see why" : "Nothing failed"}
      />
    </KpiGrid>
  );

  const seg = (value: StatusFilter, label: string, n: number) => (
    <SegButton type="button" $on={status === value} aria-pressed={status === value} onClick={() => setStatus(value)}>
      {label}
      <span className="n">{n}</span>
    </SegButton>
  );

  const filters = (
    <Filters>
      <Segmented role="group" aria-label="Status">
        {seg("active", "All active", active.length)}
        {seg("running", "Running", running.length)}
        {seg("queued", "Queued", queued.length)}
        {seg("finished", "Recently finished", finished.length)}
      </Segmented>
      <Select aria-label="Type" value={type} onChange={e => setType(e.target.value as TypeFilter)}>
        <option value="all">All types</option>
        <option value="replica">Replicas</option>
        <option value="migration">Migrations</option>
        <option value="deployment">Deployments</option>
      </Select>
      <Select aria-label="Source cloud" value={source} onChange={e => setSource(e.target.value)}>
        <option value="">Any source</option>
        {sourceTypes.map(t => (
          <option key={t} value={t}>
            {providerLabel(t)}
          </option>
        ))}
      </Select>
      <Select aria-label="Target cloud" value={target} onChange={e => setTarget(e.target.value)}>
        <option value="">Any target</option>
        {targetTypes.map(t => (
          <option key={t} value={t}>
            {providerLabel(t)}
          </option>
        ))}
      </Select>
      {projectIds.length > 1 ? (
        <Select aria-label="Project" value={project} onChange={e => setProject(e.target.value)}>
          <option value="">All projects</option>
          {projectIds.map(id => (
            <option key={id} value={id}>
              {props.projects.find(p => p.id === id)?.name || id.slice(0, 8)}
            </option>
          ))}
        </Select>
      ) : null}
      <SearchBox>
        <Search size={15} color={LIVE.faint} aria-hidden="true" />
        <input
          type="search"
          placeholder="Search transfers, machines, endpoints"
          aria-label="Search"
          value={query}
          onChange={e => setQuery(e.target.value)}
        />
      </SearchBox>
    </Filters>
  );

  let list: React.ReactNode;
  if (!props.loaded) {
    list = (
      <EmptyCard>
        <EmptyState icon={Gauge} title="Looking for running migrations…" compact />
      </EmptyCard>
    );
  } else if (!shown.length) {
    const nothingAtAll = !active.length && status !== "finished" && !filtered;
    list = (
      <EmptyCard>
        {nothingAtAll ? (
          <EmptyState
            icon={Gauge}
            title="Nothing is moving right now"
            message={
              finished.length
                ? `${finished.length} run${finished.length === 1 ? "" : "s"} finished in the last 24 hours. Start a sync from a transfer, or create a new one.`
                : "Syncs and migrations show up here the moment they start, with live throughput and time left."
            }
            actionLabel="Create a transfer"
            onAction={props.onCreateTransfer}
            secondaryLabel={finished.length ? "Recently finished" : undefined}
            onSecondary={() => setStatus("finished")}
          />
        ) : (
          <EmptyState
            icon={Search}
            tone="neutral"
            title="Nothing matches these filters"
            message="Try another status, type or cloud, or clear the search."
            compact
          />
        )}
      </EmptyCard>
    );
  } else {
    list = (
      <Cards>
        {shown.map(e => (
          <LiveExecutionCard key={e.key} entry={e} samples={props.samples[e.key] || []} now={now} />
        ))}
      </Cards>
    );
  }

  return (
    <Wrapper>
      {kpis}
      {filters}
      {status === "finished" && shown.length ? <SectionTitle>Finished in the last 24 hours</SectionTitle> : null}
      {list}
      {props.lastUpdated ? <Updated>Live · updated {formatClock(props.lastUpdated, now)} · refreshes every 5 s</Updated> : null}
    </Wrapper>
  );
});

export default LiveMonitorContent;
